import {
  type CreateEphemeralDashboardTokenRequest,
  type CreateEphemeralDashboardTokenResponse,
} from '@qdrant/qdrant-cloud-public-api/serverless/space/auth/v1/ephemeral_dashboard_token';
import { type QueryClient } from '@tanstack/react-query';
import { StatusCodes } from 'http-status-codes';
import { type ConfiguredMiddleware, type FetchLike, type WretchOptions } from 'wretch';
import { spaceDashboardTokenService } from '@/api/services/serverless/space-dashboard-token';
import { type MessageInput } from '@/connect-query';

type SpaceTokenOptions = { client: QueryClient } & MessageInput<CreateEphemeralDashboardTokenRequest>;

/** The cached token, or a fresh one when it went stale. Read-only or manage, after the caller's `*:serverless_data`. */
async function getToken({ client, ...params }: SpaceTokenOptions): Promise<string> {
  const { ephemeralDashboardToken } = await client.fetchQuery(
    spaceDashboardTokenService.createEphemeralDashboardTokenQuery(params),
  );
  if (!ephemeralDashboardToken?.token) {
    throw new Error('The space dashboard token was not returned.');
  }
  return ephemeralDashboardToken.token;
}

/**
 * Marks the cached token stale, so the next request mints a fresh one. Only when the cache still holds `token`:
 * another request may already have minted its replacement. A mint already in flight is joined, not cancelled.
 */
function dropToken({ client, ...params }: SpaceTokenOptions, token: string) {
  const { queryKey } = spaceDashboardTokenService.createEphemeralDashboardTokenQuery(params);
  if (client.getQueryData<CreateEphemeralDashboardTokenResponse>(queryKey)?.ephemeralDashboardToken?.token !== token) {
    return;
  }
  void client.invalidateQueries({ queryKey });
}

/**
 * Sends the space dashboard token as `api-key`. An expired or revoked token answers 401: mint a fresh one and try
 * once more. Added per request, with the `client` of the query or mutation function.
 */
export const spaceTokenMiddleware =
  (options: SpaceTokenOptions): ConfiguredMiddleware =>
  (next: FetchLike) =>
  async (url: string, opts: WretchOptions) => {
    const send = (token: string) => {
      const headers = new Headers(opts.headers);
      headers.set('api-key', token);
      return next(url, { ...opts, headers });
    };

    const token = await getToken(options);
    const response = await send(token);
    // `response.status` is a plain number, and `StatusCodes` only names the 401.
    // oxlint-disable-next-line typescript/no-unsafe-enum-comparison
    if (response.status !== StatusCodes.UNAUTHORIZED) {
      return response;
    }

    dropToken(options, token);
    return send(await getToken(options));
  };
